import { fileCabinetCarriedDocumentId } from './file-cabinet.references';
import {
    CabinetCall,
    CabinetDocument,
    FileCabinetPage,
    FileCabinetRecord,
    fileCabinetRecordId,
    FileCabinetRequestError,
    FileCabinetViewKey,
} from './file-cabinet.types';

/**
 * Filtering for demo mode. The demo generators sort and page but ignore filters, so a demo search
 * would show the whole generated list under a search that looks applied (NS-3). Filters with a plain
 * meaning are applied here; anything else — full text, dates, numbers — is refused, said as such,
 * rather than faked (NS-4, NS-14).
 *
 * Semantics follow the API's, as measured: values comma-joined in one parameter are ORed, and so are
 * repeated parameters (sandbox, 2026-09-23 and 2026-10-22).
 */

export const FILE_CABINET_DEMO_UNSUPPORTED = 'This search or filter is not available in demo mode.';

type Matcher = (view: FileCabinetViewKey, record: FileCabinetRecord) => string[];

/** What each supported filter compares against. */
const FIELDS: Record<string, { methods: string[]; values: Matcher }> = {
    filterId: { methods: ['EQ'], values: (view, record) => compact([fileCabinetRecordId(view, record)]) },
    filterDocumentType: {
        methods: ['EQ'],
        values: (view, record) =>
            compact([view === 'docs' ? (record as CabinetDocument).documentType : documentOf(record)?.documentType]),
    },
    filterFilename: {
        methods: ['LK'],
        values: (view, record) => compact([view === 'docs' ? (record as CabinetDocument).filename : documentOf(record)?.filename]),
    },
    filterClaimId: {
        methods: ['EQ'],
        values: (view, record) => {
            const document = view === 'docs' ? (record as CabinetDocument) : documentOf(record);
            return compact([...(document?.claimIds ?? []), document?.claimId]);
        },
    },
    filterDocumentId: { methods: ['EQ'], values: (view, record) => compact([fileCabinetCarriedDocumentId(view, record)]) },
    filterSourceDocumentId: { methods: ['EQ'], values: (_view, record) => (record as CabinetDocument).sourceDocumentIds ?? [] },
    filterMethod: { methods: ['EQ'], values: (_view, record) => compact([(record as CabinetCall).method]) },
};

function documentOf(record: FileCabinetRecord): CabinetDocument | undefined {
    return (record as { document?: CabinetDocument }).document;
}

function compact(values: (string | undefined)[]): string[] {
    return values.filter((value): value is string => !!value);
}

/** `["EQ:a,EQ:b", "EQ:c"]` → `[["EQ","a"],["EQ","b"],["EQ","c"]]`. */
function conditions(wire: string[]): [string, string][] {
    return wire
        .flatMap(entry => entry.split(','))
        .map(part => {
            const at = part.indexOf(':');
            return [part.slice(0, at), part.slice(at + 1)] as [string, string];
        });
}

/** Throws the "not available in demo" error for a filter this cannot apply honestly. */
export function fileCabinetDemoFilter<T extends FileCabinetRecord>(
    view: FileCabinetViewKey,
    records: T[],
    filters: Record<string, string[]>,
): T[] {
    const active = Object.entries(filters).filter(([, wire]) => wire.length > 0);
    for (const [filterId, wire] of active) {
        const field = FIELDS[filterId];
        if (!field || conditions(wire).some(([method]) => !field.methods.includes(method))) {
            throw new FileCabinetRequestError(FILE_CABINET_DEMO_UNSUPPORTED);
        }
    }
    return records.filter(record =>
        active.every(([filterId, wire]) => {
            const have = FIELDS[filterId].values(view, record);
            return conditions(wire).some(([method, value]) =>
                method === 'LK'
                    ? have.some(field => field.toLowerCase().includes(value.toLowerCase()))
                    : have.includes(value),
            );
        }),
    );
}

/** One page of already sorted, already filtered demo records. */
export function fileCabinetDemoPage<T>(records: T[], limit: number, offset: number): FileCabinetPage<T> {
    return { items: records.slice(offset, offset + limit), total: records.length };
}

/**
 * The field each API sort value orders by. The demo generators turn the sort value itself into a field
 * name (`RECEIVED` → `received`), which matches no mail field, so their sort silently does nothing.
 */
const SORT_FIELDS: Record<string, string> = {
    CREATED: 'created',
    FILENAME: 'filename',
    RECEIVED: 'receivedDate',
    SCANNED: 'scanDate',
    BARCODE: 'barcode',
    PAGES: 'pages',
    EXTERNALID: 'externalId',
    START_DATE: 'start',
    END_DATE: 'end',
    COMPLETED_DATE: 'completedDate',
};

/** Sorted as the API would: by the field the sort value names; records without it last. */
export function fileCabinetDemoSort<T>(records: T[], sort: string | undefined, order: 'ASCENDING' | 'DESCENDING' | undefined): T[] {
    const field = sort ? SORT_FIELDS[sort] : undefined;
    if (!field) {
        return records;
    }
    const direction = order === 'ASCENDING' ? 1 : -1;
    return [...records].sort((a, b) => {
        const x = (a as Record<string, unknown>)[field];
        const y = (b as Record<string, unknown>)[field];
        if (x === undefined || x === null) {
            return y === undefined || y === null ? 0 : 1;
        }
        if (y === undefined || y === null) {
            return -1;
        }
        return (x < y ? -1 : x > y ? 1 : 0) * direction;
    });
}
